// module
import React, { useEffect } from 'react';
// typings
import { INotificationAction } from 'modules/NotificationCenter/typings';
// ui library
import { Button, View, InfoIcon, WarningIcon, Text } from '@upstox/upstox-ui';
// constants
import { EVENTS } from 'modules/Analytics/consts';
// services
import AnalyticsService from 'modules/Analytics';
// components
import ModalPagination from '../ModalPagination';
import { Props } from './typings';
import SanitizeHTML from './components/SanitizeHTML';
// styles
import * as css from './styles';

const ModalNotification: React.FC<Props> = ({
  notification,
  handleAction,
  next,
  previous,
  totalCount,
  index,
}) => {
  const getContent = () => {
    const { image, message, title, theme } = notification;

    const getAlertIconByType = (type: string) => {
      switch (type) {
        case 'warning':
          return <WarningIcon size="xxlarge" color="background.yellow" />;
        case 'critical':
          return <WarningIcon size="xxlarge" color="background.orange" />;
        case 'info':
          return <InfoIcon size="xxlarge" color="icon2" />;
        case 'none':
        default:
          return <React.Fragment />;
      }
    };

    return (
      <View css={css.contentContainer}>
        {getAlertIconByType(theme)}
        {image ? (
          <img
            src={image}
            alt={title}
            draggable={false}
            style={{
              borderRadius: 8,
              maxHeight: 500,
              width: '100%',
            }}
          />
        ) : (
          <React.Fragment />
        )}
        <Text style={{ fontSize: 16 }} weight="bold" lineHeight="l">
          {title}
        </Text>
        <SanitizeHTML html={message} />
      </View>
    );
  };

  const getActionButtons = () => {
    const { primary, secondary } = notification.actions;

    const getButton = (action: INotificationAction) => {
      const isSecondary = action?.theme === 'secondary';

      return (
        <Button
          onClick={() => handleAction(action, notification)}
          style={{
            height: '38px',
            width: !secondary ? '100%' : '50%',
          }}
          variant={!isSecondary ? 'primary' : 'secondary'}
          autoFocus={!isSecondary}
        >
          {action.text}
        </Button>
      );
    };

    return (
      <View flex justifyContent="space-evenly" alignItems="center" gap="medium">
        {secondary ? getButton(secondary) : <React.Fragment />}
        {primary ? getButton(primary) : <React.Fragment />}
      </View>
    );
  };

  useEffect(() => {
    if (notification) {
      AnalyticsService.sendEvent(EVENTS.CUSTOM_IN_APP_NOTIFICATION_SHOWN, {
        'Notification Type': 'fullscreen',
        'Notification ID': notification.id,
        'Notification Identifier': notification.identifier || '',
        dismissible: notification.dismissable,
        'Notification Version': notification.version || 1,
      });
    }
  }, [notification]);

  return (
    <View css={css.modalContainer}>
      <View css={css.modalDialog}>
        <View flexDirection="column" css={css.base}>
          <View css={css.modalHeader}>
            <ModalPagination
              currentIndex={index}
              totalCount={totalCount}
              previous={previous}
              next={next}
            />
          </View>
          <View css={css.modalBody}>{getContent()}</View>
          <View css={css.modalFooter}>{getActionButtons()}</View>
        </View>
      </View>
    </View>
  );
};

export default ModalNotification;
